import React, { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Breadcrumbs,
  Link,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add,
  Security,
  Home,
  Edit,
  Delete,
  Person,
  Group,
  Key,
  People,
  Map as MapIcon,
  Refresh,
  Search,
} from '@mui/icons-material';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../../hooks/useApi';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { MarketAccessTab } from '../../components/admin/MarketAccessTab';

interface User {
  id: string;
  email: string;
  name: string;
  created_at: string;
  updated_at: string;
  last_login_at: string;
  is_active: boolean;
  roles: Role[];
}

interface Role {
  id: string;
  name: string;
  description?: string;
  created_at: string;
  permissions?: Permission[];
  user_count?: number;
}

interface Permission {
  id: string;
  name: string;
  resource?: string;
  action?: string;
  description?: string;
  created_at: string;
  role_count?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`permissions-tabpanel-${index}`}
      aria-labelledby={`permissions-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Map tab names to indices
const TAB_NAME_TO_INDEX: Record<string, number> = {
  users: 0,
  roles: 1,
  permissions: 2,
  'market-access': 3,
};

const TAB_INDEX_TO_NAME: Record<number, string> = {
  0: 'users',
  1: 'roles',
  2: 'permissions',
  3: 'market-access',
};

export const PermissionsPage: React.FC = () => {
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial tab from querystring, default to 'users' (0)
  const initialTab = TAB_NAME_TO_INDEX[searchParams.get('tab') || 'users'] ?? 0;
  const [tabValue, setTabValue] = useState(initialTab);

  // Sync tab with URL when navigating from sidebar links
  useEffect(() => {
    const tabFromUrl = TAB_NAME_TO_INDEX[searchParams.get('tab') || 'users'] ?? 0;
    if (tabFromUrl !== tabValue) {
      setTabValue(tabFromUrl);
    }
  }, [searchParams]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [userRolesDialogOpen, setUserRolesDialogOpen] = useState(false);
  const [rolePermissionsDialogOpen, setRolePermissionsDialogOpen] = useState(false);
  const [roleUsersDialogOpen, setRoleUsersDialogOpen] = useState(false);
  const [createPermissionDialogOpen, setCreatePermissionDialogOpen] = useState(false);
  const [editPermissionDialogOpen, setEditPermissionDialogOpen] = useState(false);
  const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState(false);
  const [newPermission, setNewPermission] = useState({ id: '', name: '', resource: '', action: '', description: '' });
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [newRole, setNewRole] = useState({ id: '', name: '', description: '' });
  const [editingRole, setEditingRole] = useState<{ name: string; description: string } | null>(null);
  const [selectedRolePermissions, setSelectedRolePermissions] = useState<string[]>([]);
  const [selectedUserRoles, setSelectedUserRoles] = useState<string[]>([]);
  
  // Search state for Users tab
  const [userSearchTerm, setUserSearchTerm] = useState<string>('');
  
  // Sorting and filtering state for permissions
  const [permissionsOrderBy, setPermissionsOrderBy] = useState<keyof Permission>('name');
  const [permissionsOrder, setPermissionsOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedResource, setSelectedResource] = useState<string>('all');

  // Show loading spinner while user data is being fetched
  if (isLoadingUser) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have permissions.all permission (after loading completes)
  if (!user?.permissions?.includes('permissions.all')) {
    return <Navigate to="/" replace />;
  }

  // Fetch users
  const { data: usersData, isLoading: usersLoading, refetch: refetchUsers } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/users`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch users');
      }
      return await response.json();
    }
  });

  // Fetch roles
  const { data: rolesData, isLoading: rolesLoading, refetch: refetchRoles } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/roles`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch roles');
      }
      return await response.json();
    }
  });

  // Fetch permissions
  const { data: permissionsData, isLoading: permissionsLoading, refetch: refetchPermissions } = useQuery({
    queryKey: ['permissions'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/permissions`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch permissions');
      }
      return await response.json();
    }
  });

  // Update user roles mutation
  const updateUserRolesMutation = useMutation({
    mutationFn: async ({ userId, roleIds }: { userId: string; roleIds: string[] }) => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/users/${userId}/roles`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ roleIds }),
      });
      if (!response || !response.ok) {
        throw new Error('Failed to update user roles');
      }
      return await response.json();
    },
    onSuccess: () => {
      refetchUsers();
      refetchRoles(); // Also refresh roles to update user counts
      // Invalidate currentUser cache to refresh permissions
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      setUserRolesDialogOpen(false);
      setSelectedUserRoles([]);
    },
  });

  // Update role permissions mutation
  const updateRolePermissionsMutation = useMutation({
    mutationFn: async ({ roleId, permissionIds }: { roleId: string; permissionIds: string[] }) => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/roles/${roleId}/permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ permissionIds }),
      });
      if (!response || !response.ok) {
        throw new Error('Failed to update role permissions');
      }
      return await response.json();
    },
    onSuccess: () => {
      refetchRoles();
      // Invalidate currentUser cache to refresh permissions
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      setRolePermissionsDialogOpen(false);
      setSelectedRolePermissions([]);
    },
  });

  // Update role details mutation
  const updateRoleDetailsMutation = useMutation({
    mutationFn: async ({ roleId, name, description }: { roleId: string; name: string; description?: string }) => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/roles/${encodeURIComponent(roleId)}/details`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, description }),
      });
      if (!response || !response.ok) {
        throw new Error('Failed to update role details');
      }
      return await response.json();
    },
    onSuccess: () => {
      refetchRoles();
      // Invalidate currentUser cache to refresh permissions
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      setEditingRole(null);
    },
  });

  // Update permission mutation
  const updatePermissionMutation = useMutation({
    mutationFn: async (permission: Partial<Permission> & { id: string }) => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/permissions/${encodeURIComponent(permission.id)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(permission),
      });
      if (!response || !response.ok) {
        throw new Error('Failed to update permission');
      }
      return await response.json();
    },
    onSuccess: () => {
      refetchPermissions();
      // Invalidate currentUser cache to refresh permissions
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      setEditPermissionDialogOpen(false);
      setEditingPermission(null);
    },
  });

  // Create permission mutation
  const createPermissionMutation = useMutation({
    mutationFn: async (permission: typeof newPermission) => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/permissions/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(permission),
      });
      if (!response || !response.ok) {
        throw new Error('Failed to create permission');
      }
      return await response.json();
    },
    onSuccess: () => {
      refetchPermissions();
      // Invalidate currentUser cache to refresh permissions
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      setCreatePermissionDialogOpen(false);
      setNewPermission({ id: '', name: '', resource: '', action: '', description: '' });
    },
  });

  // Create role mutation
  const createRoleMutation = useMutation({
    mutationFn: async (role: typeof newRole) => {
      const response = await authenticatedFetch(`${API_URL}/api/portal-admin/roles/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(role),
      });
      if (!response || !response.ok) {
        throw new Error('Failed to create role');
      }
      return await response.json();
    },
    onSuccess: () => {
      refetchRoles();
      // Invalidate currentUser cache to refresh permissions
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      setCreateRoleDialogOpen(false);
      setNewRole({ id: '', name: '', description: '' });
    },
  });

  const users = useMemo(() => {
    const allUsers = usersData?.data || [];
    if (!userSearchTerm) {
      return allUsers;
    }
    const searchLower = userSearchTerm.toLowerCase();
    return allUsers.filter((user: User) => 
      user.email.toLowerCase().includes(searchLower) ||
      (user.name && user.name.toLowerCase().includes(searchLower))
    );
  }, [usersData, userSearchTerm]);
  
  const roles = (rolesData?.data || []).sort((a: Role, b: Role) => a.name.localeCompare(b.name));
  
  // Get unique resources for filter dropdown
  const uniqueResources = useMemo(() => {
    const resources = new Set<string>();
    (permissionsData?.data || []).forEach((perm: Permission) => {
      if (perm.resource) {
        resources.add(perm.resource);
      }
    });
    return Array.from(resources).sort();
  }, [permissionsData]);
  
  // Handle permission sorting
  const handlePermissionSort = (property: keyof Permission) => {
    const isAsc = permissionsOrderBy === property && permissionsOrder === 'asc';
    setPermissionsOrder(isAsc ? 'desc' : 'asc');
    setPermissionsOrderBy(property);
  };
  
  // Apply filtering and sorting to permissions for the Permissions tab
  const permissions = useMemo(() => {
    let filtered = permissionsData?.data || [];
    
    // Apply resource filter
    if (selectedResource !== 'all') {
      filtered = filtered.filter((perm: Permission) => perm.resource === selectedResource);
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a: Permission, b: Permission) => {
      const aValue = a[permissionsOrderBy] || '';
      const bValue = b[permissionsOrderBy] || '';
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return permissionsOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return permissionsOrder === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
      
      return 0;
    });
    
    return sorted;
  }, [permissionsData, selectedResource, permissionsOrderBy, permissionsOrder]);

  // Build unfiltered, grouped permissions for the Edit Role dialog
  const permissionsForDialog = useMemo((): Array<{ resource: string; items: Permission[] }> => {
    const allPerms: Permission[] = (permissionsData?.data || []) as Permission[];
    // Group by resource
    const groups: Map<string, Permission[]> = new Map<string, Permission[]>();
    for (const p of allPerms) {
      const key = p.resource || 'general';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(p);
    }
    // Sort groups by resource name and permissions by name
    const sortedGroupKeys: string[] = Array.from(groups.keys()).sort();
    const sortedGroups: Array<{ resource: string; items: Permission[] }> = sortedGroupKeys.map((resource: string) => ({
      resource,
      items: groups.get(resource)!.slice().sort((a: Permission, b: Permission) => (a.name || '').localeCompare(b.name || ''))
    }));
    return sortedGroups;
  }, [permissionsData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Update URL with new tab parameter
    const tabName = TAB_INDEX_TO_NAME[newValue];
    if (tabName) {
      setSearchParams({ tab: tabName }, { replace: true });
    }
  };

  const handleRefresh = () => {
    // Refresh data based on current tab
    switch (tabValue) {
      case 0: // Users tab
        refetchUsers();
        break;
      case 1: // Roles tab
        refetchRoles();
        break;
      case 2: // Permissions tab
        refetchPermissions();
        break;
      case 3: // Market Access tab - handled by its own component
        // The MarketAccessTab component should handle its own refresh
        break;
    }
  };

  const handleEditUserRoles = (user: User) => {
    setSelectedUser(user);
    setSelectedUserRoles(user.roles?.map(r => r.id) || []);
    setUserRolesDialogOpen(true);
  };

  const handleEditRolePermissions = (role: Role) => {
    setSelectedRole(role);
    setEditingRole({ name: role.name, description: role.description || '' });
    setSelectedRolePermissions(role.permissions?.map(p => p.id) || []);
    setRolePermissionsDialogOpen(true);
  };

  const handleViewRoleUsers = (role: Role) => {
    setSelectedRole(role);
    setRoleUsersDialogOpen(true);
  };

  const handleSaveUserRoles = (selectedRoleIds: string[]) => {
    if (selectedUser) {
      updateUserRolesMutation.mutate({ userId: selectedUser.id, roleIds: selectedRoleIds });
    }
  };

  const handleSaveRolePermissions = (selectedPermissionIds: string[]) => {
    if (selectedRole) {
      updateRolePermissionsMutation.mutate({ roleId: selectedRole.id, permissionIds: selectedPermissionIds });
    }
  };

  const handleRemoveUserFromRole = (userId: string) => {
    if (selectedRole) {
      const user = users.find((u: User) => u.id === userId);
      if (user && user.roles) {
        const newRoleIds = user.roles
          .filter((r: Role) => r.id !== selectedRole.id)
          .map((r: Role) => r.id);
        updateUserRolesMutation.mutate({ userId, roleIds: newRoleIds });
      }
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <Security sx={{ mr: 0.5 }} fontSize="inherit" />
            Permissions
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Portal Permissions Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage users, roles, and permissions for the portal
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper elevation={1}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="permissions tabs">
              <Tab icon={<Person />} label="Users" iconPosition="start" />
              <Tab icon={<Group />} label="Roles" iconPosition="start" />
              <Tab icon={<Key />} label="Permissions" iconPosition="start" />
              <Tab icon={<MapIcon />} label="Market Access" iconPosition="start" />
            </Tabs>
            <Box sx={{ pr: 2 }}>
              <Tooltip title="Refresh">
                <IconButton 
                  onClick={handleRefresh}
                  disabled={
                    (tabValue === 0 && usersLoading) ||
                    (tabValue === 1 && rolesLoading) ||
                    (tabValue === 2 && permissionsLoading)
                  }
                >
                  <Refresh 
                    sx={{
                      animation: (
                        (tabValue === 0 && usersLoading) ||
                        (tabValue === 1 && rolesLoading) ||
                        (tabValue === 2 && permissionsLoading)
                      ) ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                      }
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Users Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 2 }}>
              <TextField
                placeholder="Search by email or name..."
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ width: 300 }}
              />
            </Box>
            {usersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : users.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  {userSearchTerm ? `No users found matching "${userSearchTerm}"` : 'No users found'}
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Roles</TableCell>
                      <TableCell>Active</TableCell>
                      <TableCell>Last Login</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user: User) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.name || '-'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {user.roles && user.roles.length > 0 ? (
                              user.roles.map((role) => (
                                <Chip key={role.id} label={role.name} size="small" color="primary" />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">No roles</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={user.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : 'Never'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit Roles">
                            <IconButton onClick={() => handleEditUserRoles(user)} size="small">
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Roles Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setCreateRoleDialogOpen(true)}
              >
                Add New Role
              </Button>
            </Box>
            {rolesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Role ID</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Permissions</TableCell>
                      <TableCell>Users</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roles.map((role: Role) => (
                      <TableRow key={role.id}>
                        <TableCell>{role.name}</TableCell>
                        <TableCell>
                          <Chip label={role.id} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 250,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={role.description || '-'}
                        >
                          {role.description || '-'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {role.permissions && role.permissions.length > 0 ? (
                              role.permissions.slice(0, 3).map((perm) => (
                                <Chip key={perm.id} label={perm.name} size="small" variant="outlined" />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">No permissions</Typography>
                            )}
                            {role.permissions && role.permissions.length > 3 && (
                              <Typography variant="body2" color="text.secondary">
                                +{role.permissions.length - 3} more
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{role.user_count || 0}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Edit Permissions">
                            <IconButton onClick={() => handleEditRolePermissions(role)} size="small">
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Users">
                            <IconButton onClick={() => handleViewRoleUsers(role)} size="small">
                              <People />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Permissions Tab */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="resource-filter-label">Filter by Resource</InputLabel>
                <Select
                  labelId="resource-filter-label"
                  value={selectedResource}
                  label="Filter by Resource"
                  onChange={(e: SelectChangeEvent) => setSelectedResource(e.target.value)}
                  size="small"
                >
                  <MenuItem value="all">All Resources</MenuItem>
                  {uniqueResources.map((resource) => (
                    <MenuItem key={resource} value={resource}>
                      {resource}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setCreatePermissionDialogOpen(true)}
              >
                Add New Permission
              </Button>
            </Box>
            {permissionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={permissionsOrderBy === 'name' ? permissionsOrder : false}>
                        <TableSortLabel
                          active={permissionsOrderBy === 'name'}
                          direction={permissionsOrderBy === 'name' ? permissionsOrder : 'asc'}
                          onClick={() => handlePermissionSort('name')}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={permissionsOrderBy === 'id' ? permissionsOrder : false}>
                        <TableSortLabel
                          active={permissionsOrderBy === 'id'}
                          direction={permissionsOrderBy === 'id' ? permissionsOrder : 'asc'}
                          onClick={() => handlePermissionSort('id')}
                        >
                          Permission ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={permissionsOrderBy === 'resource' ? permissionsOrder : false}>
                        <TableSortLabel
                          active={permissionsOrderBy === 'resource'}
                          direction={permissionsOrderBy === 'resource' ? permissionsOrder : 'asc'}
                          onClick={() => handlePermissionSort('resource')}
                        >
                          Resource
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={permissionsOrderBy === 'action' ? permissionsOrder : false}>
                        <TableSortLabel
                          active={permissionsOrderBy === 'action'}
                          direction={permissionsOrderBy === 'action' ? permissionsOrder : 'asc'}
                          onClick={() => handlePermissionSort('action')}
                        >
                          Action
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={permissionsOrderBy === 'description' ? permissionsOrder : false}>
                        <TableSortLabel
                          active={permissionsOrderBy === 'description'}
                          direction={permissionsOrderBy === 'description' ? permissionsOrder : 'asc'}
                          onClick={() => handlePermissionSort('description')}
                        >
                          Description
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={permissionsOrderBy === 'role_count' ? permissionsOrder : false}>
                        <TableSortLabel
                          active={permissionsOrderBy === 'role_count'}
                          direction={permissionsOrderBy === 'role_count' ? permissionsOrder : 'asc'}
                          onClick={() => handlePermissionSort('role_count')}
                        >
                          Used by Roles
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {permissions.map((permission: Permission) => (
                      <TableRow key={permission.id}>
                        <TableCell 
                          sx={{ 
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {permission.name}
                        </TableCell>
                        <TableCell>{permission.id}</TableCell>
                        <TableCell>{permission.resource || '-'}</TableCell>
                        <TableCell>{permission.action || '-'}</TableCell>
                        <TableCell 
                          sx={{ 
                            maxWidth: 250,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={permission.description || '-'}
                        >
                          {permission.description || '-'}
                        </TableCell>
                        <TableCell>{permission.role_count || 0}</TableCell>
                        <TableCell>
                          <Tooltip title="Edit Permission">
                            <IconButton 
                              onClick={() => {
                                setEditingPermission(permission);
                                setEditPermissionDialogOpen(true);
                              }} 
                              size="small"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Market Access Tab */}
          <TabPanel value={tabValue} index={3}>
            <MarketAccessTab />
          </TabPanel>
        </Paper>

        {/* User Roles Dialog */}
        <Dialog open={userRolesDialogOpen} onClose={() => {
          setUserRolesDialogOpen(false);
          setSelectedUserRoles([]);
        }} maxWidth="sm" fullWidth>
          <DialogTitle>Edit User Roles - {selectedUser?.email}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {roles.map((role: Role) => (
                <FormControlLabel
                  key={role.id}
                  control={
                    <Checkbox
                      checked={selectedUserRoles.includes(role.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUserRoles([...selectedUserRoles, role.id]);
                        } else {
                          setSelectedUserRoles(selectedUserRoles.filter(id => id !== role.id));
                        }
                      }}
                      value={role.id}
                    />
                  }
                  label={
                    <Box>
                      <Typography>{role.name}</Typography>
                      {role.description && (
                        <Typography variant="caption" color="text.secondary">
                          {role.description}
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{ width: '100%', mb: 1 }}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setUserRolesDialogOpen(false);
              setSelectedUserRoles([]);
            }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                handleSaveUserRoles(selectedUserRoles);
              }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Role Permissions Dialog */}
        <Dialog open={rolePermissionsDialogOpen} onClose={() => {
          setRolePermissionsDialogOpen(false);
          setEditingRole(null);
          setSelectedRolePermissions([]);
        }} maxWidth="md" fullWidth>
          <DialogTitle>Edit Role - {selectedRole?.name}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Role Details Section */}
              <Box>
                <Typography variant="h6" gutterBottom>Role Details</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Role ID"
                    value={selectedRole?.id || ''}
                    disabled
                    fullWidth
                    helperText="ID cannot be changed"
                  />
                  <TextField
                    label="Name"
                    value={editingRole?.name || ''}
                    onChange={(e) => setEditingRole(editingRole ? { ...editingRole, name: e.target.value } : null)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Description"
                    value={editingRole?.description || ''}
                    onChange={(e) => setEditingRole(editingRole ? { ...editingRole, description: e.target.value } : null)}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Box>
              </Box>
              
              {/* Permissions Section */}
              <Box>
                <Typography variant="h6" gutterBottom>Permissions</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {permissionsForDialog.map(group => (
                    <Box key={group.resource}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        {group.resource}
                      </Typography>
                      <Grid container spacing={1}>
                        {group.items.map((permission: Permission) => (
                          <Grid item xs={12} sm={6} key={permission.id}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedRolePermissions.includes(permission.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedRolePermissions([...selectedRolePermissions, permission.id]);
                                    } else {
                                      setSelectedRolePermissions(selectedRolePermissions.filter(id => id !== permission.id));
                                    }
                                  }}
                                  value={permission.id}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">{permission.name}</Typography>
                                  {permission.description && (
                                    <Typography variant="caption" color="text.secondary">
                                      {permission.description}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setRolePermissionsDialogOpen(false);
              setEditingRole(null);
              setSelectedRolePermissions([]);
            }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={async () => {
                if (selectedRole && editingRole) {
                  // Update role details if they changed
                  if (editingRole.name !== selectedRole.name || editingRole.description !== selectedRole.description) {
                    await updateRoleDetailsMutation.mutateAsync({
                      roleId: selectedRole.id,
                      name: editingRole.name,
                      description: editingRole.description
                    });
                  }
                  
                  // Update permissions using the state
                  handleSaveRolePermissions(selectedRolePermissions);
                }
              }}
              disabled={!editingRole?.name}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Permission Dialog */}
        <Dialog open={editPermissionDialogOpen} onClose={() => {
          setEditPermissionDialogOpen(false);
          setEditingPermission(null);
        }} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Permission</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Permission ID"
                value={editingPermission?.id || ''}
                disabled
                fullWidth
                helperText="ID cannot be changed"
              />
              <TextField
                label="Name"
                value={editingPermission?.name || ''}
                onChange={(e) => setEditingPermission(editingPermission ? { ...editingPermission, name: e.target.value } : null)}
                fullWidth
                required
                helperText="Display name (e.g., 'Create Users')"
              />
              <TextField
                label="Resource"
                value={editingPermission?.resource || ''}
                onChange={(e) => setEditingPermission(editingPermission ? { ...editingPermission, resource: e.target.value } : null)}
                fullWidth
                helperText="Resource type (e.g., 'users')"
              />
              <TextField
                label="Action"
                value={editingPermission?.action || ''}
                onChange={(e) => setEditingPermission(editingPermission ? { ...editingPermission, action: e.target.value } : null)}
                fullWidth
                helperText="Action type (e.g., 'create', 'read', 'update', 'delete')"
              />
              <TextField
                label="Description"
                value={editingPermission?.description || ''}
                onChange={(e) => setEditingPermission(editingPermission ? { ...editingPermission, description: e.target.value } : null)}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setEditPermissionDialogOpen(false);
              setEditingPermission(null);
            }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                if (editingPermission) {
                  updatePermissionMutation.mutate({
                    id: editingPermission.id,
                    name: editingPermission.name,
                    resource: editingPermission.resource,
                    action: editingPermission.action,
                    description: editingPermission.description
                  });
                }
              }}
              disabled={!editingPermission?.name}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Permission Dialog */}
        <Dialog open={createPermissionDialogOpen} onClose={() => setCreatePermissionDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Permission</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Permission ID"
                value={newPermission.id}
                onChange={(e) => setNewPermission({ ...newPermission, id: e.target.value })}
                fullWidth
                required
                helperText="Unique identifier (e.g., 'users.create')"
              />
              <TextField
                label="Name"
                value={newPermission.name}
                onChange={(e) => setNewPermission({ ...newPermission, name: e.target.value })}
                fullWidth
                required
                helperText="Display name (e.g., 'Create Users')"
              />
              <TextField
                label="Resource"
                value={newPermission.resource}
                onChange={(e) => setNewPermission({ ...newPermission, resource: e.target.value })}
                fullWidth
                helperText="Resource type (e.g., 'users')"
              />
              <TextField
                label="Action"
                value={newPermission.action}
                onChange={(e) => setNewPermission({ ...newPermission, action: e.target.value })}
                fullWidth
                helperText="Action type (e.g., 'create', 'read', 'update', 'delete')"
              />
              <TextField
                label="Description"
                value={newPermission.description}
                onChange={(e) => setNewPermission({ ...newPermission, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreatePermissionDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => createPermissionMutation.mutate(newPermission)}
              disabled={!newPermission.id || !newPermission.name}
            >
              Create Permission
            </Button>
          </DialogActions>
        </Dialog>

        {/* Role Users Dialog */}
        <Dialog open={roleUsersDialogOpen} onClose={() => setRoleUsersDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Users in Role - {selectedRole?.name}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {selectedRole && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Other Roles</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users
                        .filter((user: User) => user.roles && user.roles.some(r => r.id === selectedRole.id))
                        .map((user: User) => (
                          <TableRow key={user.id}>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.name || '-'}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {user.roles && user.roles
                                  .filter(r => r.id !== selectedRole.id)
                                  .map(role => (
                                    <Chip key={role.id} label={role.name} size="small" variant="outlined" />
                                  ))}
                                {(!user.roles || user.roles.filter(r => r.id !== selectedRole.id).length === 0) && (
                                  <Typography variant="body2" color="text.secondary">None</Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Remove from Role">
                                <IconButton 
                                  onClick={() => handleRemoveUserFromRole(user.id)} 
                                  size="small"
                                  color="error"
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {selectedRole && users.filter((user: User) => user.roles && user.roles.some(r => r.id === selectedRole.id)).length === 0 && (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No users have this role
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRoleUsersDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Create Role Dialog */}
        <Dialog open={createRoleDialogOpen} onClose={() => setCreateRoleDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Role</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Role ID"
                value={newRole.id}
                onChange={(e) => setNewRole({ ...newRole, id: e.target.value })}
                fullWidth
                required
                helperText="Unique identifier (e.g., 'editor', 'viewer')"
              />
              <TextField
                label="Name"
                value={newRole.name}
                onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                fullWidth
                required
                helperText="Display name (e.g., 'Editor', 'Viewer')"
              />
              <TextField
                label="Description"
                value={newRole.description}
                onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateRoleDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => createRoleMutation.mutate(newRole)}
              disabled={!newRole.id || !newRole.name}
            >
              Create Role
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};